import React from 'react';
import { useSettings } from '../context/SettingsProvider';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ThemeSwitcherProps {
    /** Show only toggle button (no dropdown) */
    variant?: 'toggle' | 'dropdown';
    /** Additional class names */
    className?: string;
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({
    variant = 'toggle',
    className = '',
}) => {
    const { theme, setTheme, resolvedTheme, toggleTheme } = useSettings();
    const { t } = useTranslation();

    // Simple toggle button
    if (variant === 'toggle') {
        return (
            <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg hover:bg-accent transition-colors ${className}`}
                title={resolvedTheme === 'dark' ? t('settings.lightMode') : t('settings.darkMode')}
                aria-label={resolvedTheme === 'dark' ? t('settings.lightMode') : t('settings.darkMode')}
            >
                {resolvedTheme === 'dark' ? (
                    <Sun className="h-5 w-5 text-foreground" />
                ) : (
                    <Moon className="h-5 w-5 text-foreground" />
                )}
            </button>
        );
    }

    // Dropdown with light/dark/system options
    return (
        <div className={`flex items-center gap-1 p-1 bg-muted rounded-lg ${className}`}>
            <button
                onClick={() => setTheme('light')}
                className={`p-2 rounded-md transition-colors ${theme === 'light'
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                title={t('settings.lightMode')}
                aria-label={t('settings.lightMode')}
            >
                <Sun className="h-4 w-4" />
            </button>
            <button
                onClick={() => setTheme('dark')}
                className={`p-2 rounded-md transition-colors ${theme === 'dark'
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                title={t('settings.darkMode')}
                aria-label={t('settings.darkMode')}
            >
                <Moon className="h-4 w-4" />
            </button>
            <button
                onClick={() => setTheme('system')}
                className={`p-2 rounded-md transition-colors ${theme === 'system'
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                title="System"
                aria-label="System theme"
            >
                <Monitor className="h-4 w-4" />
            </button>
        </div>
    );
};
